import React, { useState, useEffect } from 'react';
import '../styles/Dashboard.css';

function ProfileModal({ isOpen, onClose, onSave, currentUser, onExportSuccess, onExportError }) {
  const [formData, setFormData] = useState({
    name: '',
    notificationPreferences: {
      email: true,
      days: [30, 14, 7]
    }
  });

  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (currentUser) {
      console.log('ProfileModal received currentUser:', currentUser);
      console.log('notificationPreferences:', currentUser.notificationPreferences);
      
      setFormData({
        name: currentUser.name || '',
        notificationPreferences: currentUser.notificationPreferences || {
          email: true,
          days: [30, 14, 7]
        }
      });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const days = prev.notificationPreferences.days;
      const newDays = days.includes(day)
        ? days.filter(d => d !== day)
        : [...days, day].sort((a, b) => b - a);
      
      return {
        ...prev,
        notificationPreferences: {
          ...prev.notificationPreferences,
          days: newDays
        }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Please enter your name');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      alert('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('kinde_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Download the ZIP file (but don't auto-open)
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'certifiable-data-export.zip';
      a.style.display = 'none'; // Hide the link
      document.body.appendChild(a);
      a.click();
      
      // Clean up immediately
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      // Show success via callback (will use toast in Dashboard)
      if (onExportSuccess) {
        onExportSuccess();
      }
    } catch (error) {
      console.error('Export error:', error);
      if (onExportError) {
        onExportError();
      }
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem('kinde_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Deletion failed');
      }

      alert('Your account has been deleted. You will now be logged out.');
      
      // Clear local storage and redirect to logout
      localStorage.clear();
      window.location.href = '/api/auth/logout';
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete account. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '540px' }}>
        <div className="modal-header">
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--ink)', margin: 0 }}>Profile Settings</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ paddingTop: '24px' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
              <small className="form-helper">
                This name will appear when you email certificates to employers
              </small>
            </div>

            <div className="form-group" style={{ marginTop: '32px' }}>
              <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                Email Reminders
              </label>
              <small className="form-helper" style={{ marginBottom: '12px', display: 'block' }}>
                Get reminded before your certificates expire
              </small>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '8px'
              }}>
                {[30, 14, 7, 3, 1].map(day => (
                  <label 
                    key={day}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '10px 14px',
                      border: '2px solid',
                      borderRadius: '8px',
                      backgroundColor: formData.notificationPreferences.days.includes(day) ? '#eff6ff' : 'white',
                      borderColor: formData.notificationPreferences.days.includes(day) ? '#1e3a8a' : '#e5e7eb',
                      transition: 'all 0.2s',
                      fontWeight: 500,
                      fontSize: '14px'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.notificationPreferences.days.includes(day)}
                      onChange={() => handleDayToggle(day)}
                      style={{ margin: 0, width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--primary)' }}
                    />
                    <span>
                      {day} day{day !== 1 ? 's' : ''}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #bfdbfe', 
              borderRadius: '8px', 
              padding: '14px 16px',
              marginTop: '24px',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '18px' }}>💡</span>
              <p style={{ margin: 0, fontSize: '13px', color: '#1e3a8a', lineHeight: '1.5' }}>
                <strong>Tip:</strong> We recommend enabling reminders at 30, 14, and 7 days before expiry
              </p>
            </div>

            {/* Account Management Section */}
            <div style={{ 
              marginTop: '32px', 
              paddingTop: '24px', 
              borderTop: '2px solid var(--line)'
            }}>
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: 'var(--ink)', 
                marginBottom: '16px' 
              }}>
                Account Management
              </h3>

              {/* Export Data Button */}
              <button
                type="button"
                onClick={handleExportData}
                disabled={exporting}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: '8px',
                  border: '2px solid var(--primary)',
                  borderRadius: '6px',
                  background: 'white',
                  color: 'var(--primary)',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: exporting ? 'not-allowed' : 'pointer',
                  opacity: exporting ? 0.6 : 1,
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span style={{ fontSize: '16px' }}>📦</span>
                {exporting ? 'Exporting...' : 'Export My Data'}
              </button>
              <p style={{ 
                fontSize: '12px', 
                color: 'var(--muted)', 
                marginBottom: '16px',
                lineHeight: '1.4'
              }}>
                Download all certificates and data as ZIP
              </p>

              {/* Delete Account Section */}
              {!showDeleteConfirm ? (
                <>
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #dc2626',
                      borderRadius: '6px',
                      background: 'white',
                      color: '#dc2626',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>⚠️</span>
                    Delete My Account
                  </button>
                  <p style={{ 
                    fontSize: '12px', 
                    color: 'var(--muted)', 
                    marginTop: '8px',
                    lineHeight: '1.4'
                  }}>
                    Permanently delete account and all data
                  </p>
                </>
              ) : (
                <div style={{
                  background: '#fef2f2',
                  border: '2px solid #dc2626',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <p style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#991b1b',
                    marginBottom: '12px'
                  }}>
                    ⚠️ Are you absolutely sure?
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: '#7f1d1d',
                    marginBottom: '16px',
                    lineHeight: '1.5'
                  }}>
                    This will permanently delete your account, all certificates, and uploaded files. This action cannot be undone.
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: '2px solid #d1d5db',
                        borderRadius: '8px',
                        background: 'white',
                        color: 'var(--text)',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: deleting ? 'not-allowed' : 'pointer',
                        opacity: deleting ? 0.6 : 1
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: 'none',
                        borderRadius: '8px',
                        background: '#dc2626',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: deleting ? 'not-allowed' : 'pointer',
                        opacity: deleting ? 0.6 : 1
                      }}
                    >
                      {deleting ? 'Deleting...' : 'Yes, Delete Everything'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileModal;
